import { Label } from "@/shared/components/ui/label";
import { Button } from "@/shared/components/ui/button";
import { Shield, ChevronDown, ChevronRight, CheckSquare, XSquare } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/shared/components/ui/tooltip";
import { useState } from "react";

export interface ModulePermissions {
  visualizar: boolean;
  incluir: boolean;
  alterar: boolean;
  excluir: boolean;
}

export interface UserPermissionsData {
  dashboard: ModulePermissions;
  formatacao: ModulePermissions;
  peticoes: ModulePermissions;
  documentos: ModulePermissions;
  meus_documentos: ModulePermissions;
  criacao_ia: ModulePermissions;
  ai_training: ModulePermissions;
  tipos_documentos: ModulePermissions;
  financeiro: ModulePermissions;
  fin_clientes: ModulePermissions;
  fin_lancamentos: ModulePermissions;
  fin_contratos: ModulePermissions;
  fin_gestao_contratos: ModulePermissions;
  fin_fornecedores: ModulePermissions;
  fin_fechamento: ModulePermissions;
  fin_producao: ModulePermissions;
  fin_profissionais: ModulePermissions;
  fin_rentabilidade: ModulePermissions;
  fin_inadimplencia: ModulePermissions;
  fin_quitacao: ModulePermissions;
  fin_vencimentos: ModulePermissions;
  fin_rateio_templates: ModulePermissions;
  fin_ia: ModulePermissions;
  fin_relatorios: ModulePermissions;
  fin_captadores_externos: ModulePermissions;
  calculos: ModulePermissions;
  calc_tributario: ModulePermissions;
  calc_trabalhista: ModulePermissions;
  calc_monetario: ModulePermissions;
  calc_prazos: ModulePermissions;
  auditoria: ModulePermissions;
  audit_tributaria: ModulePermissions;
  audit_trabalhista: ModulePermissions;
  normas_procedimentos: ModulePermissions;
  normas_ia: ModulePermissions;
  processos: ModulePermissions;
  instituicoes: ModulePermissions;
  usuarios: ModulePermissions;
  permissoes: ModulePermissions;
  relatorios: ModulePermissions;
  administracao: ModulePermissions;
  manual: ModulePermissions;
  sistema: ModulePermissions;
  gestao_juridica: ModulePermissions;       // Sidebar: Gestão Jurídica
  gestao_administrativa: ModulePermissions; // Sidebar: Gestão Administrativa
  [key: string]: ModulePermissions;
}

const defaultPermissions: ModulePermissions = {
  visualizar: false,
  incluir: false,
  alterar: false,
  excluir: false,
};

export const createDefaultPermissions = (): UserPermissionsData => ({
  dashboard: { ...defaultPermissions },
  formatacao: { ...defaultPermissions },
  peticoes: { ...defaultPermissions },
  documentos: { ...defaultPermissions },
  meus_documentos: { ...defaultPermissions },
  criacao_ia: { ...defaultPermissions },
  ai_training: { ...defaultPermissions },
  tipos_documentos: { ...defaultPermissions },
  financeiro: { ...defaultPermissions },
  fin_clientes: { ...defaultPermissions },
  fin_lancamentos: { ...defaultPermissions },
  fin_contratos: { ...defaultPermissions },
  fin_gestao_contratos: { ...defaultPermissions },
  fin_fornecedores: { ...defaultPermissions },
  fin_fechamento: { ...defaultPermissions },
  fin_producao: { ...defaultPermissions },
  fin_profissionais: { ...defaultPermissions },
  fin_rentabilidade: { ...defaultPermissions },
  fin_inadimplencia: { ...defaultPermissions },
  fin_quitacao: { ...defaultPermissions },
  fin_vencimentos: { ...defaultPermissions },
  fin_rateio_templates: { ...defaultPermissions },
  fin_ia: { ...defaultPermissions },
  fin_relatorios: { ...defaultPermissions },
  fin_captadores_externos: { ...defaultPermissions },
  calculos: { ...defaultPermissions },
  calc_tributario: { ...defaultPermissions },
  calc_trabalhista: { ...defaultPermissions },
  calc_monetario: { ...defaultPermissions },
  calc_prazos: { ...defaultPermissions },
  auditoria: { ...defaultPermissions },
  audit_tributaria: { ...defaultPermissions },
  audit_trabalhista: { ...defaultPermissions },
  normas_procedimentos: { ...defaultPermissions },
  normas_ia: { ...defaultPermissions },
  processos: { ...defaultPermissions },
  instituicoes: { ...defaultPermissions },
  usuarios: { ...defaultPermissions },
  permissoes: { ...defaultPermissions },
  relatorios: { ...defaultPermissions },
  administracao: { ...defaultPermissions },
  manual: { ...defaultPermissions },
  sistema: { ...defaultPermissions },
  gestao_juridica: { ...defaultPermissions },
  gestao_administrativa: { ...defaultPermissions },
});

const moduleDescriptions: Record<string, string> = {
  dashboard: "Tela inicial com resumo de atividades, estatísticas e atalhos rápidos para os módulos do sistema.",
  formatacao: "Módulo de formatação inteligente de documentos jurídicos, incluindo upload, correção e exportação.",
  peticoes: "Configuração de templates, seções e modelos utilizados na formatação de petições e documentos.",
  documentos: "Gerenciamento de templates de documento, seções de formatação e modelos de petição. Cada usuário só pode alterar/excluir seus próprios registros.",
  meus_documentos: "Histórico de documentos formatados. Cada usuário vê e gerencia apenas seus próprios documentos. Admins (Sócio Gestor) têm visão global. 'Excluir' remove da lista pessoal (soft delete).",
  criacao_ia: "Criação de petições, contratos e procurações por inteligência artificial com o motor Jurist AI.",
  ai_training: "Alimentação da base de conhecimento da IA com documentos de referência do escritório.",
  tipos_documentos: "Gerenciamento de tipos de documentos da IA: criar, editar e desativar tipos customizados para expandir as capacidades de criação inteligente.",
  financeiro: "Acesso geral ao módulo financeiro. Controla a visibilidade do menu e da área financeira.",
  fin_clientes: "Cadastro e gestão de clientes financeiros, incluindo honorários, captadores e categorias de fee.",
  fin_lancamentos: "Lançamentos mensais de receita por cliente, com valores bruto, tributos e pós-desconto.",
  fin_contratos: "Contratos vinculados a clientes com controle de parcelas, IPCA e natureza jurídica.",
  fin_gestao_contratos: "Gestão e monitoramento de contratos de prestação de serviços. Permite receber alertas de clientes sem contrato cadastrado e notificar responsáveis.",
  fin_fornecedores: "Cadastro de fornecedores do escritório com dados fiscais, endereço e valores.",
  fin_producao: "Relatórios de produção por profissional e área, baseados nos lançamentos mensais.",
  fin_fechamento: "Fechamento mensal financeiro com distribuição de valores entre sócios e profissionais.",
  fin_profissionais: "Gestão de compensações, ajudas de custo e reembolsos dos profissionais do escritório.",
  fin_rentabilidade: "Análise de rentabilidade do escritório comparando receitas e despesas.",
  fin_inadimplencia: "Controle de parcelas e pagamentos em atraso de clientes.",
  fin_quitacao: "Permite autorizar a quitação antecipada de contratos parcelados antes do término previsto das parcelas.",
  fin_vencimentos: "Visualizar e gerenciar alertas de vencimentos contratuais e parcelas.",
  fin_rateio_templates: "Criar, editar e excluir templates de quota de rateio de honorários.",
  fin_ia: "Assistente de inteligência artificial para análise e consultas financeiras.",
  fin_relatorios: "Relatórios financeiros consolidados, gráficos e exportações.",
  fin_captadores_externos: "Cadastro e gestão de captadores externos com dados bancários, OAB e informações de pagamento.",
  calculos: "Acesso geral ao hub de cálculos. Controla a visibilidade do módulo de calculadoras.",
  calc_tributario: "Calculadoras tributárias: ICMS, ISS, IPI, PIS/COFINS, IRPJ/CSLL, Simples Nacional, DIFAL, ICMS-ST.",
  calc_trabalhista: "Calculadoras trabalhistas: rescisão, férias, 13º salário, horas extras e custo trabalhista.",
  calc_monetario: "Correção monetária simples e comparativa utilizando índices econômicos oficiais.",
  calc_prazos: "Calculadora de prazos processuais com feriados e suspensões por jurisdição.",
  auditoria: "Acesso geral ao hub de auditorias. Controla a visibilidade do módulo de auditoria.",
  audit_tributaria: "Auditoria tributária: importação de SPED/XML, cruzamentos fiscais, achados e créditos.",
  audit_trabalhista: "Auditoria trabalhista: importação de folhas/ponto, análise de conformidade e riscos.",
  normas_procedimentos: "Gestão de normas internas, regimentos, POPs e circulares do escritório.",
  normas_ia: "Acesso ao Oráculo — assistente de IA especializado em normas e procedimentos.",
  processos: "Listagem e gestão de processos judiciais e administrativos do escritório.",
  instituicoes: "Cadastro de instituições (condomínios, associações, sindicatos, empresas) com base de documentos própria. A IA de cada instituição responde apenas com base nos documentos cadastrados.",
  usuarios: "Cadastro, edição e exclusão de contas de usuários do sistema.",
  permissoes: "Poder de conceder ou revogar permissões de acesso de outros usuários. Atenção: permissão sensível.",
  relatorios: "Logs de atividade do sistema, sessões de usuários e histórico de ações realizadas.",
  administracao: "Painel administrativo com datas comemorativas, história do escritório, feedback e erros do sistema.",
  manual: "Documentação e guia de uso do sistema.",
  sistema: "Configurações gerais do sistema, integrações, perfil e parâmetros técnicos.",
  gestao_juridica: "Hub de Gestão Jurídica: processos, criação de documentos por IA, formatação inteligente e arquivo digital do escritório.",
  gestao_administrativa: "Hub de Gestão Administrativa: documentos IA de atendimento interno, normas e procedimentos, administração, sistema e gestão de arquivos.",
};

const opDescriptions: Record<keyof ModulePermissions, string> = {
  visualizar: "Permite acessar e ver os dados deste módulo. Sem esta permissão, o módulo ficará invisível ou bloqueado.",
  incluir: "Permite criar novos registros neste módulo (ex: cadastrar clientes, criar documentos, adicionar lançamentos).",
  alterar: "Permite editar registros existentes neste módulo (ex: atualizar dados, modificar valores, corrigir informações).",
  excluir: "Permite remover registros deste módulo. Esta é a permissão mais sensível — use com cautela.",
};

interface PermissionGroup {
  label: string;
  modules: { key: keyof UserPermissionsData; label: string; indent?: boolean }[];
}

const permissionGroups: PermissionGroup[] = [
  {
    label: "🏛️ Hubs de Gestão (Sidebar)",
    modules: [
      { key: "gestao_juridica", label: "Gestão Jurídica" },
      { key: "gestao_administrativa", label: "Gestão Administrativa" },
    ],
  },
  {
    label: "🛡️ Núcleo de Produção e IA",
    modules: [
      { key: "criacao_ia", label: "Criação por IA (Geral)" },
      { key: "formatacao", label: "Formatação Inteligente" },
      { key: "documentos", label: "Gestão de Templates e Modelos" },
      { key: "meus_documentos", label: "Histórico de Documentos" },
      { key: "ai_training", label: "Treinamento e Fontes da IA" },
      { key: "tipos_documentos", label: "Tipos de Documentos IA" },
      { key: "peticoes", label: "Configuração de Petições", indent: true },
      { key: "processos", label: "Jurídico Ferrugini Advogados" },
    ],
  },
  {
    label: "💰 Núcleo Financeiro (Completo)",
    modules: [
      { key: "financeiro", label: "Dashboard Financeiro" },
      { key: "fin_clientes", label: "Clientes", indent: true },
      { key: "fin_contratos", label: "Contratos e Setup", indent: true },
      { key: "fin_gestao_contratos", label: "Gestão de Contratos (Alertas)", indent: true },
      { key: "fin_lancamentos", label: "Lançamentos de Receita", indent: true },
      { key: "fin_fechamento", label: "Fechamento Mensal", indent: true },
      { key: "fin_producao", label: "Relatórios de Produção", indent: true },
      { key: "fin_profissionais", label: "Profissionais e Compensações", indent: true },
      { key: "fin_rentabilidade", label: "Rentabilidade", indent: true },
      { key: "fin_inadimplencia", label: "Inadimplência", indent: true },
      { key: "fin_quitacao", label: "Quitação Antecipada", indent: true },
      { key: "fin_vencimentos", label: "Vencimentos Contratuais", indent: true },
      { key: "fin_fornecedores", label: "Fornecedores", indent: true },
      { key: "fin_captadores_externos", label: "Captadores Externos", indent: true },
      { key: "fin_rateio_templates", label: "Modelos de Rateio", indent: true },
      { key: "fin_ia", label: "Assistente IA Financeiro", indent: true },
      { key: "fin_relatorios", label: "Relatórios Financeiros", indent: true },
    ],
  },
  {
    label: "⚖️ Cálculos, Auditoria e Compliance",
    modules: [
      { key: "calculos", label: "Hub de Cálculos" },
      { key: "calc_trabalhista", label: "Cálculos Trabalhistas", indent: true },
      { key: "calc_tributario", label: "Cálculos Tributários", indent: true },
      { key: "calc_monetario", label: "Correção Monetária", indent: true },
      { key: "calc_prazos", label: "Prazos Processuais", indent: true },
      { key: "auditoria", label: "Dashboard de Auditorias" },
      { key: "audit_trabalhista", label: "Auditoria Trabalhista", indent: true },
      { key: "audit_tributaria", label: "Auditoria Tributária", indent: true },
      { key: "normas_procedimentos", label: "Normas Internas (POPs)" },
      { key: "normas_ia", label: "Oráculo de Normas (IA)", indent: true },
      { key: "instituicoes", label: "Instituições (Bases por Cliente)", indent: true },
    ],
  },
  {
    label: "⚙️ Governança do Sistema",
    modules: [
      { key: "usuarios", label: "Gestão de Usuários" },
      { key: "permissoes", label: "Controle de Acessos" },
      { key: "relatorios", label: "Logs de Auditoria" },
      { key: "administracao", label: "Painel Administrativo" },
      { key: "sistema", label: "Configurações do Sistema" },
      { key: "manual", label: "Manual do Sistema" },
      { key: "dashboard", label: "Dashboard (Início)", indent: true },
    ],
  },
];

const opLabels: Record<keyof ModulePermissions, string> = {
  visualizar: "Visualizar",
  incluir: "Incluir",
  alterar: "Alterar",
  excluir: "Excluir",
};

interface UserPermissionsProps {
  permissions: UserPermissionsData;
  onChange: (permissions: UserPermissionsData) => void;
  readOnly?: boolean;
}

const UserPermissions = ({ permissions: rawPermissions, onChange, readOnly = false }: UserPermissionsProps) => {
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});

  const defaults = createDefaultPermissions();
  const permissions: UserPermissionsData = {
    ...defaults,
    ...Object.fromEntries(
      Object.entries(rawPermissions ?? {}).map(([k, v]) => [k, { ...defaultPermissions, ...(v ?? {}) }])
    ),
  } as UserPermissionsData;

  const togglePermission = (module: keyof UserPermissionsData, op: keyof ModulePermissions) => {
    if (readOnly) return;
    const updated = { ...permissions };
    updated[module] = { ...updated[module], [op]: !updated[module][op] };
    onChange(updated);
  };

  const toggleModuleAll = (module: keyof UserPermissionsData) => {
    if (readOnly) return;
    const allChecked = Object.values(permissions[module]).every(Boolean);
    const val = !allChecked;
    const updated = { ...permissions };
    updated[module] = { visualizar: val, incluir: val, alterar: val, excluir: val };
    onChange(updated);
  };

  const toggleModuleCrud = (module: keyof UserPermissionsData) => {
    if (readOnly) return;
    const crudChecked = permissions[module].incluir && permissions[module].alterar && permissions[module].excluir;
    const val = !crudChecked;
    const updated = { ...permissions };
    updated[module] = { ...updated[module], incluir: val, alterar: val, excluir: val };
    onChange(updated);
  };

  const toggleGroupAll = (group: PermissionGroup, grant: boolean) => {
    if (readOnly) return;
    const updated = { ...permissions };
    for (const mod of group.modules) {
      updated[mod.key] = { visualizar: grant, incluir: grant, alterar: grant, excluir: grant };
    }
    onChange(updated);
  };

  const toggleGroup = (label: string) => {
    setCollapsedGroups((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  const isGroupAllChecked = (group: PermissionGroup) =>
    group.modules.every((m) => Object.values(permissions[m.key]).every(Boolean));

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-1">
        <Shield className="h-4 w-4 text-primary" />
        <Label className="font-display text-sm font-semibold">Permissões de Acesso</Label>
      </div>

      {permissionGroups.map((group) => {
        const collapsed = collapsedGroups[group.label];
        const groupAll = isGroupAllChecked(group);

        return (
          <div key={group.label} className="border rounded-lg overflow-hidden">
            {/* Group Header */}
            <div
              className="flex items-center justify-between bg-muted/70 px-3 py-2 cursor-pointer select-none"
              onClick={() => toggleGroup(group.label)}
            >
              <div className="flex items-center gap-2">
                {collapsed ? <ChevronRight className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
                <span className="text-sm font-semibold text-foreground">{group.label}</span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${groupAll ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"}`}>
                  {group.modules.length} módulos
                </span>
              </div>
              {!readOnly && !collapsed && (
                <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                  <Button type="button" variant="ghost" size="sm" className="h-6 px-2 text-[10px]" onClick={() => toggleGroupAll(group, true)}>
                    <CheckSquare className="h-3 w-3 mr-1 text-primary" />
                    Todas
                  </Button>
                  <Button type="button" variant="ghost" size="sm" className="h-6 px-2 text-[10px]" onClick={() => toggleGroupAll(group, false)}>
                    <XSquare className="h-3 w-3 mr-1 text-destructive" />
                    Nenhuma
                  </Button>
                </div>
              )}
            </div>

            {!collapsed && (
              <>
                {/* Column Header */}
                <div className="grid grid-cols-[1fr_60px_60px_60px_60px_70px] bg-muted/40 text-[10px] font-semibold text-muted-foreground px-3 py-1.5 border-b">
                  <span>Módulo</span>
                  {(Object.keys(opLabels) as (keyof ModulePermissions)[]).map((op) => (
                    <Tooltip key={op}>
                      <TooltipTrigger asChild>
                        <span className="text-center cursor-help">{opLabels[op]}</span>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-[260px] text-xs">
                        <p className="font-semibold mb-0.5">{opLabels[op]}</p>
                        <p className="text-muted-foreground">{opDescriptions[op]}</p>
                      </TooltipContent>
                    </Tooltip>
                  ))}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="text-center cursor-help">CIE</span>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-[240px] text-xs">
                      <p className="font-semibold mb-0.5">CIE — Criar, Incluir, Excluir</p>
                      <p className="text-muted-foreground">Alterna de uma vez as permissões de Incluir, Alterar e Excluir para este módulo.</p>
                    </TooltipContent>
                  </Tooltip>
                </div>

                {/* Rows */}
                {group.modules.map((mod, i) => {
                  const allChecked = Object.values(permissions[mod.key]).every(Boolean);
                  const crudChecked = permissions[mod.key].incluir && permissions[mod.key].alterar && permissions[mod.key].excluir;
                  return (
                    <div
                      key={mod.key}
                      className={`grid grid-cols-[1fr_60px_60px_60px_60px_70px] items-center px-3 py-1.5 text-sm ${i % 2 === 0 ? "bg-background" : "bg-muted/15"} hover:bg-muted/30 transition-colors`}
                    >
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            onClick={() => toggleModuleAll(mod.key)}
                            disabled={readOnly}
                            className={`text-left text-xs font-medium transition-colors ${mod.indent ? "pl-4" : ""} ${allChecked ? "text-primary" : "text-foreground"} ${!readOnly ? "hover:text-primary cursor-pointer" : ""}`}
                          >
                            {mod.indent && <span className="text-muted-foreground mr-1">└</span>}
                            {mod.label}
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="right" className="max-w-[300px] text-xs">
                          <p className="font-semibold mb-0.5">{mod.label}</p>
                          <p className="text-muted-foreground">{moduleDescriptions[mod.key]}</p>
                          {!readOnly && <p className="text-primary mt-1 text-[10px]">Clique para marcar/desmarcar todas</p>}
                        </TooltipContent>
                      </Tooltip>

                      {(Object.keys(opLabels) as (keyof ModulePermissions)[]).map((op) => (
                        <div key={op} className="flex justify-center">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div>
                                <input
                                  type="checkbox"
                                  checked={permissions[mod.key][op]}
                                  onChange={() => togglePermission(mod.key, op)}
                                  disabled={readOnly}
                                  className="h-4 w-4 rounded border-[#a9abae] accent-[#791614] cursor-pointer disabled:opacity-50"
                                />
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-[280px] text-xs">
                              <p className="font-semibold mb-0.5">{opLabels[op]} — {mod.label}</p>
                              <p className="text-muted-foreground">{opDescriptions[op]}</p>
                            </TooltipContent>
                          </Tooltip>
                        </div>
                      ))}

                      <div className="flex justify-center">
                        {!readOnly && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                type="button"
                                onClick={() => toggleModuleCrud(mod.key)}
                                className={`text-[9px] px-1.5 py-0.5 rounded font-semibold transition-colors ${
                                  crudChecked
                                    ? "bg-primary/15 text-primary hover:bg-primary/25"
                                    : "bg-muted text-muted-foreground hover:bg-muted/80"
                                }`}
                              >
                                {crudChecked ? "✓ CIE" : "CIE"}
                              </button>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-[240px] text-xs">
                              <p className="font-semibold mb-0.5">Alternar Incluir + Alterar + Excluir</p>
                              <p className="text-muted-foreground">Concede ou revoga de uma só vez as permissões de criação, edição e exclusão para "{mod.label}".</p>
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default UserPermissions;
